import { useCallback, useEffect, useState } from 'react'
import { PencilIcon, TrashIcon } from '@heroicons/react/outline'

import { LoadingCircle } from 'components/Loading'

import { classNames } from 'utils'
import { ClientAddress } from 'models'
import { EditAddress } from './components/Edit'
import { ReadResult } from 'interfaces/queryOptions'
import { useEditClient } from 'modules/Clients/hooks/useEditClient'
import { translations } from 'modules/Clients/translations'
import { useConfirmationDialog } from 'components/ConfirmationDialog'

export function AddressesTab() {
  const {
    isLoadingAddress,
    client,
    fetchClient,
    addresses,
    listAddresses,
    deleteAddress
  } = useEditClient()

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const [showConfirmationDialog] = useConfirmationDialog()

  useEffect(() => {
    if (!client) {
      fetchClient()
    }
  }, [client, fetchClient])

  useEffect(() => {
    if (!addresses) {
      listAddresses()
    }
  }, [addresses, listAddresses])

  const onDelete = useCallback(
    async (id: number) => {
      await deleteAddress(id)
    },
    [deleteAddress]
  )

  const onRemoveAddress = (id: number) => {
    showConfirmationDialog({
      title: 'Remover endereço',
      message: 'Tem certeza que deseja remover o endereço?',
      onConfirm: () => onDelete(id)
    })
  }

  return (
    <>
      <div className="w-10/12 px-4 mt-6 animate-fade-in-down">
        {isLoadingAddress ? (
          <LoadingCircle />
        ) : (
          addresses &&
          addresses.length > 0 && (
            <div className="-mx-4 ring-1 ring-gray-300 sm:-mx-6 md:mx-0 md:rounded-lg animate-fade-in-down">
              <table className="min-w-full divide-y divide-gray-300">
                <tbody>
                  {addresses.map(
                    (address: ReadResult<ClientAddress>, idx: number) => (
                      <tr key={idx}>
                        <td
                          className={classNames(
                            idx === 0 ? '' : 'border-t border-transparent',
                            'relative py-4 pl-4 sm:pl-6 pr-3 text-sm'
                          )}
                        >
                          <div className="font-medium text-gray-900">
                            {
                              translations['address_type'][
                                address?.relationships?.address.attributes
                                  .type as string
                              ]
                            }
                          </div>
                          {idx !== 0 ? (
                            <div className="absolute right-0 left-6 -top-px h-px bg-gray-200" />
                          ) : null}
                        </td>
                        <td
                          className={classNames(
                            idx === 0 ? '' : 'border-t border-gray-200',
                            'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                          )}
                        >
                          {
                            address.relationships?.address.attributes
                              ?.formatted_address
                          }
                        </td>
                        <td
                          className={classNames(
                            idx === 0 ? '' : 'border-t border-transparent',
                            'relative py-3.5 pl-3 pr-4 sm:pr-6 text-right text-sm font-medium'
                          )}
                        >
                          <div className="flex flex-row justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setIndex(idx)
                                setIsEditOpen(true)
                              }}
                              className={`-ml-px relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-emerald-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-emerald-500 ${
                                addresses && addresses.length > 1
                                  ? 'rounded-l-md'
                                  : 'rounded-md'
                              }`}
                            >
                              <PencilIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                            {addresses && addresses.length > 1 && (
                              <button
                                type="button"
                                onClick={() => onRemoveAddress(idx)}
                                className="-ml-px relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-red-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-emerald-500"
                              >
                                <TrashIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </div>
                          {idx !== 0 ? (
                            <div className="absolute right-6 left-0 -top-px h-px bg-gray-200" />
                          ) : null}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <EditAddress open={isEditOpen} setOpen={setIsEditOpen} index={index} />
    </>
  )
}
